/**
 * NYC Property Information Service
 * Fetches property data from NYC GeoService API and Property Information Portal
 *
 * API Key Setup:
 * 1. Register at: https://geoservice.planning.nyc.gov/
 * 2. Add API key to .env file: VITE_NYC_GEOSERVICE_API_KEY=your_key_here
 */

import { NYCPropertyInfo, NYCCondoInfo, NYCCondoUnit } from '@/types/nyc';

const NYC_PROPERTY_PORTAL_BASE = 'https://propertyinformationportal.nyc.gov';
const NYC_GEOSERVICE_BASE = 'https://geoservice.planning.nyc.gov/geoservice/geoservice.svc';

// Get API key from environment variable
const NYC_API_KEY = import.meta.env.VITE_NYC_GEOSERVICE_API_KEY;

/**
 * Extract BBL (Borough-Block-Lot) from address using NYC geocoding
 * @param address - Full address string
 * @returns BBL string or null
 */
export async function getBBLFromAddress(address: string): Promise<string | null> {
  try {
    // NYC Planning GeoSearch API
    const geocodeUrl = `https://geosearch.planninglabs.nyc/v2/search?text=${encodeURIComponent(address)}`;
    const response = await fetch(geocodeUrl);

    if (!response.ok) {
      console.warn('NYC GeoSearch API failed:', response.status);
      return null;
    }

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      const properties = feature.properties;

      // BBL can be in multiple locations depending on the API response
      const bbl = properties.pad_bbl ||
                  properties.bbl ||
                  properties.addendum?.pad?.bbl;

      if (bbl) {
        console.log(`Found BBL ${bbl} for address: ${address}`);
        return bbl;
      }
    }

    console.warn('No BBL found in GeoSearch response for:', address);
    return null;
  } catch (error) {
    console.error('Error getting BBL from address:', error);
    return null;
  }
}

/**
 * Check if address is in NYC based on state/city
 * @param address - Nominatim address object
 * @returns true if in NYC
 */
export function isNYCAddress(address: any): boolean {
  const city = address.city?.toLowerCase() || '';
  const state = address.state?.toLowerCase() || '';

  // Check for NYC boroughs or New York state
  const nycCities = ['new york', 'brooklyn', 'queens', 'bronx', 'staten island', 'manhattan'];
  const isNYC = nycCities.some(c => city.includes(c)) && state.includes('new york');

  return isNYC;
}

/**
 * Extract condo BBL from URL (format: /parcels/condo/{bbl})
 * @param url - NYC Property Portal URL
 * @returns BBL or null
 */
function extractBBLFromURL(url: string): string | null {
  const match = url.match(/\/parcels\/condo\/(\d+)/);
  return match ? match[1] : null;
}

/**
 * Parse HTML table to extract condo units
 * This is a simplified parser - in production, you'd want a more robust solution
 * @param html - HTML content
 * @returns Array of condo units
 */
function parseCondoUnitsFromHTML(html: string): NYCCondoUnit[] {
  const units: NYCCondoUnit[] = [];

  try {
    // Look for table rows with unit data
    // Format: <tr><td>1A</td><td>1101</td><td>25</td><td>1175</td><td>2010-2011</td></tr>
    const tableRowRegex = /<tr[^>]*>\s*<td[^>]*>([^<]+)<\/td>\s*<td[^>]*>(\d+)<\/td>\s*<td[^>]*>(\d+)<\/td>\s*<td[^>]*>(\d+)<\/td>\s*<td[^>]*>([^<]+)<\/td>/g;

    let match;
    while ((match = tableRowRegex.exec(html)) !== null) {
      const [, unit, taxLot, baseLot, block, effectiveTaxYear] = match;

      // Skip header rows
      if (unit.toLowerCase() === 'unit' || taxLot === 'Tax Lot') {
        continue;
      }

      units.push({
        unit: unit.trim(),
        taxLot: taxLot.trim(),
        baseLot: baseLot.trim(),
        block: block.trim(),
        effectiveTaxYear: effectiveTaxYear.trim(),
      });
    }
  } catch (error) {
    console.error('Error parsing condo units from HTML:', error);
  }

  return units;
}

/**
 * Parse condo information from HTML
 * @param html - HTML content
 * @returns Condo info object
 */
function parseCondoInfoFromHTML(html: string): Partial<NYCCondoInfo> | null {
  try {
    const info: Partial<NYCCondoInfo> = {};

    // Extract condo number (format: <span>302584</span>)
    const condoNumMatch = html.match(/Condominium Number[^>]*>\s*<[^>]*>([^<]+)<\//i);
    if (condoNumMatch) {
      info.condominiumNumber = condoNumMatch[1].trim();
    }

    // Extract total units
    const totalUnitsMatch = html.match(/Total Units[^>]*>\s*<[^>]*>([^<]+)<\//i);
    if (totalUnitsMatch) {
      info.totalUnits = parseInt(totalUnitsMatch[1].trim()) || 0;
    }

    // Extract residential units
    const residentialMatch = html.match(/Residential Units[^>]*>\s*<[^>]*>([^<]+)<\//i);
    if (residentialMatch && residentialMatch[1].trim() !== '-') {
      info.residentialUnits = parseInt(residentialMatch[1].trim()) || 0;
    }

    // Extract commercial units
    const commercialMatch = html.match(/Commercial Units[^>]*>\s*<[^>]*>([^<]+)<\//i);
    if (commercialMatch && commercialMatch[1].trim() !== '-') {
      info.commercialUnits = parseInt(commercialMatch[1].trim()) || 0;
    }

    // Extract BBL
    const bblMatch = html.match(/Condo Billing BBL[^>]*>\s*<[^>]*>([^<]+)<\//i);
    if (bblMatch) {
      info.condoBillingBBL = bblMatch[1].trim();
    }

    // Extract lot number
    const lotMatch = html.match(/Lot Number[^>]*>\s*<[^>]*>([^<]+)<\//i);
    if (lotMatch) {
      info.lotNumber = lotMatch[1].trim();
    }

    return info;
  } catch (error) {
    console.error('Error parsing condo info from HTML:', error);
    return null;
  }
}

/**
 * Fetch property information using NYC GeoService API
 * @param bbl - Borough-Block-Lot identifier (10-digit string)
 * @returns Property information or null
 */
export async function fetchNYCPropertyInfoFromGeoService(bbl: string): Promise<NYCPropertyInfo | null> {
  if (!NYC_API_KEY) {
    console.warn('NYC GeoService API key not configured. Skipping NYC property lookup.');
    console.info('To enable: Register at https://geoservice.planning.nyc.gov/ and add VITE_NYC_GEOSERVICE_API_KEY to .env');
    return null;
  }

  try {
    // Parse BBL: first digit is borough, next 5 are block, last 4 are lot
    const borough = bbl.substring(0, 1);
    const block = bbl.substring(1, 6);
    const lot = bbl.substring(6, 10);

    const url = `${NYC_GEOSERVICE_BASE}/Function_BBL?Borough=${borough}&Block=${block}&Lot=${lot}&Key=${NYC_API_KEY}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.warn(`NYC GeoService API returned ${response.status} for BBL ${bbl}`);
      return null;
    }

    const data = await response.json();

    // Extract relevant property information from GeoService response
    if (data && data.bbl) {
      // Check if this is a condo
      const isCondoUnit = data.condoflag === 'C' || data.unitCount > 1;

      if (isCondoUnit) {
        console.log(`Found condo property with ${data.unitCount || 'unknown'} units`);

        return {
          address: data.houseNumber && data.firstStreetNameNormalized
            ? `${data.houseNumber} ${data.firstStreetNameNormalized}`
            : '',
          borough: data.firstBoroughName || '',
          block: data.taxBlock || block,
          lot: data.taxLot || lot,
          condoInfo: {
            condominiumNumber: data.condoNumber || '',
            condoBillingBBL: data.condoBillingBbl || bbl,
            lotNumber: lot,
            totalUnits: data.unitCount || 0,
            condoSuffix: '',
            cipLand: '',
            residentialUnits: data.residentialUnits || 0,
            buildingCategory: '',
            suffixResidentialUnits: 0,
            cipBuilding: '',
            commercialUnits: data.commercialUnits || 0,
            buildingSubCategory: '',
            suffixCommercialUnits: 0,
            units: [], // Unit details would require additional API calls
          },
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching NYC property info from GeoService:', error);
    return null;
  }
}

/**
 * Fetch property information from NYC Property Information Portal (HTML scraping fallback)
 * Note: The Property Portal is an SPA that loads data via JavaScript, so this may not work reliably
 * @param bbl - Borough-Block-Lot identifier (condo BBL)
 * @returns Property information or null
 */
export async function fetchNYCPropertyInfo(bbl: string): Promise<NYCPropertyInfo | null> {
  // Try GeoService API first if key is available
  const geoServiceData = await fetchNYCPropertyInfoFromGeoService(bbl);
  if (geoServiceData) {
    return geoServiceData;
  }

  // Note: HTML scraping from Property Portal is unreliable because it's an SPA
  // Keeping this code for reference but it won't work without additional JavaScript execution
  console.warn('HTML scraping from Property Portal not supported. Please configure NYC GeoService API key.');
  return null;
}

/**
 * Get property info from address (combines geocoding + property lookup)
 * @param address - Full address string
 * @returns Property information or null
 */
export async function getNYCPropertyFromAddress(address: string): Promise<NYCPropertyInfo | null> {
  try {
    // First, get the BBL from the address
    const bbl = await getBBLFromAddress(address);

    if (!bbl) {
      console.warn('Could not get BBL for address:', address);
      return null;
    }

    // Then fetch property info using the BBL
    const propertyInfo = await fetchNYCPropertyInfo(bbl);

    if (propertyInfo) {
      propertyInfo.address = address;
    }

    return propertyInfo;
  } catch (error) {
    console.error('Error getting NYC property from address:', error);
    return null;
  }
}

/**
 * Estimate number of floors from total units
 * This is a rough estimate - actual floor count may vary
 * @param totalUnits - Total number of units
 * @returns Estimated floor count
 */
export function estimateFloorsFromUnits(totalUnits: number): number {
  // Rough estimate: assume 4-8 units per floor for residential buildings
  const unitsPerFloor = 6;
  return Math.max(1, Math.ceil(totalUnits / unitsPerFloor));
}
